/* ============================================================
   ReferralLanding — 2f9c70ba public referral landing page.
   /r/<code|slug> (and future <slug>.domain proxy rewrites) land
   here: resolves the referring performer, shows their shop
   products, and invites the visitor to upload a model. The
   resolved code is stashed so registration captures attribution.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ProductSummary } from '../types/product';
import type { ReferralResolve } from '../types/referral';
import { stashRef } from './referralView';
import { formatRub } from '../types/product';
import { summaryCoverUrl } from './mediaGalleryView';

interface Props {
  code: string;
  onOpenProduct: (id: string) => void;
  onGoToQuote: () => void;
}

export function ReferralLanding({ code, onOpenProduct, onGoToQuote }: Props) {
  const [info, setInfo] = useState<ReferralResolve | null>(null);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resolved = await api.resolveReferral(code);
      setInfo(resolved);
      // Stash the resolved code (even for slug arrivals) so the signup
      // payload carries the attribution token.
      stashRef(window.localStorage, resolved.referralCode);
      try {
        setProducts(await api.getProducts({ providerId: resolved.providerUserId }));
      } catch {
        setProducts([]); // shop info still renders without a product grid
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-2xl)', maxWidth: 480 }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
            Ссылка недействительна
          </p>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            {error ?? 'Исполнитель по этому адресу не найден.'}
          </p>
          <button className="btn btn-primary" onClick={onGoToQuote} style={{ marginTop: 'var(--space-md)' }}>
            Перейти к печати
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          {info.logoUrl ? (
            <img
              src={info.logoUrl}
              alt={info.companyName}
              style={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--fs-h3)',
                fontWeight: 'var(--fw-semibold)',
                color: 'var(--color-primary)',
              }}
            >
              {info.companyName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <p style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>
              {info.companyName}
              {info.isVerified && (
                <span style={{ color: 'var(--color-success)', marginLeft: 'var(--space-xs)' }}>✓</span>
              )}
            </p>
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              {info.rating != null ? `★ ${info.rating.toFixed(1)} (${info.reviewCount}) · ` : ''}
              {info.productCount} товар(ов) в магазине
            </p>
          </div>
        </div>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-md)' }}>
          Магазин исполнителя. Загрузите модель — {info.companyName} будет предложен первым, выбор исполнителя остаётся за вами.
        </p>
        <button className="btn btn-primary" onClick={onGoToQuote} style={{ marginTop: 'var(--space-sm)' }}>
          Загрузить модель
        </button>
      </div>

      {products.length > 0 && (
        <>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', margin: 'var(--space-lg) 0 var(--space-sm)' }}>
            Товары магазина
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--space-md)',
            }}
          >
            {products.map((p) => (
              <button
                key={p.productId}
                type="button"
                className="card"
                onClick={() => onOpenProduct(p.productId)}
                style={{ textAlign: 'left', cursor: 'pointer', padding: 'var(--space-sm)' }}
              >
                {summaryCoverUrl(p) && (
                  <img
                    src={summaryCoverUrl(p)!}
                    alt={p.title}
                    style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                  />
                )}
                <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)', marginTop: 'var(--space-xs)' }}>
                  {p.title}
                </p>
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                  {formatRub(p.priceRub)} ₽ · {p.productionDays} дн.
                </p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
